import {
  parseTimeOnly,
  type ISchedule,
  type IUserFit,
  type OfficeWithFloors
} from "@gettactic/api";
import { useMemo } from "react";
import {
  getScheduleTimeline,
  getUsersFromSchedule
} from "../globalSearchScheduleUtils";
import { format } from "date-fns";
import { getDateFromSchedule, getTimeFromSchedule } from "@gettactic/helpers";

export interface CurrentScheduleInfo {
  type: "current";
  endTime: string;
  freeUntilText: string;
  users: IUserFit[];
  isMeetingRoom: boolean;
  meetingTitle?: string | null;
}

export interface UpcomingScheduleInfo {
  type: "upcoming";
  text: string;
}

export type ResourceScheduleInfo =
  | CurrentScheduleInfo
  | UpcomingScheduleInfo
  | null;

export function useResourceScheduleInfo(
  schedules: ISchedule[] | undefined,
  office?: OfficeWithFloors
): ResourceScheduleInfo {
  return useMemo(() => {
    if (!schedules || schedules.length === 0 || !office) return null;

    const { currentSchedule, upcomingSchedule: nextSchedule } =
      getScheduleTimeline(schedules);

    if (currentSchedule) {
      const endTime = getTimeFromSchedule(currentSchedule.end);
      const isMeetingRoom = currentSchedule.resource.type === "meeting_room";
      const currentScheduleUsers = getUsersFromSchedule(currentSchedule);

      let freeUntilText = "";
      if (nextSchedule) {
        const nextStartTime = getTimeFromSchedule(nextSchedule.start);
        freeUntilText = `will be free until ${nextStartTime}`;
      } else {
        freeUntilText = "will be free";
      }

      return {
        type: "current",
        endTime,
        freeUntilText,
        users: currentScheduleUsers,
        isMeetingRoom,
        meetingTitle: isMeetingRoom ? currentSchedule.title : undefined
      };
    }

    if (nextSchedule) {
      const formattedTime = getTimeFromSchedule(nextSchedule.start);
      return {
        type: "upcoming",
        text: `will be occupied at ${formattedTime}`
      };
    }

    return null;
  }, [schedules, office]);
}

export interface PersonScheduleInfo {
  type: "current" | "upcoming" | "past";
  schedule: ISchedule;
  text: string;
  resourceId: string;
  resourceName: string;
  officeName?: string;
  floorName?: string;
}

export function usePersonScheduleInfo(
  schedules: ISchedule[] | undefined,
  getOfficeFloorInfo: (
    officeId?: string | null,
    parentId?: string | null
  ) => {
    officeName?: string;
    floorName?: string;
  }
): PersonScheduleInfo | null {
  return useMemo(() => {
    return getScheduleInfo({ schedules, getOfficeFloorInfo });
  }, [schedules, getOfficeFloorInfo]);
}

export const getScheduleInfo = ({
  schedules,
  getOfficeFloorInfo
}: {
  schedules: ISchedule[] | undefined;
  getOfficeFloorInfo: (
    officeId?: string | null,
    parentId?: string | null
  ) => {
    officeName?: string;
    floorName?: string;
  };
}): PersonScheduleInfo | null => {
    if (!schedules || schedules.length === 0) return null;

    const { now, currentSchedule, upcomingSchedule, pastSchedule } =
      getScheduleTimeline(schedules);

    if (currentSchedule) {
      const { officeName, floorName } = getOfficeFloorInfo(
        currentSchedule.resource.office_id,
        currentSchedule.resource.parent_id
      );
      const resourceName = currentSchedule.resource.name;

      return {
        type: "current",
        schedule: currentSchedule,
        resourceName,
        officeName,
        floorName,
        text: `now at ${resourceName}${officeName ? ` (${officeName})` : ""}`,
        resourceId: currentSchedule.resource.id
      };
    }

    if (upcomingSchedule) {
      const { officeName, floorName } = getOfficeFloorInfo(
        upcomingSchedule.resource.office_id,
        upcomingSchedule.resource.parent_id
      );

      const startDateInOfficeTz = getDateFromSchedule(upcomingSchedule.start);

      const nowInOfficeTz = format(now, "yyyy-MM-dd");
      const isToday = startDateInOfficeTz === nowInOfficeTz;

      const timeStr = `${parseTimeOnly(upcomingSchedule.start)} → ${parseTimeOnly(upcomingSchedule.end)}`;

      return {
        type: "upcoming",
        schedule: upcomingSchedule,
        text: `will be in the office ${isToday ? "at" : "on"} ${timeStr}`,
        resourceId: upcomingSchedule.resource.id,
        resourceName: upcomingSchedule.resource.name,
        officeName,
        floorName
      };
    }

    if (pastSchedule) {
      const { officeName, floorName } = getOfficeFloorInfo(
        pastSchedule.resource.office_id,
        pastSchedule.resource.parent_id
      );

      const endDate = new Date(pastSchedule.end);
      const endInOfficeTz = format(endDate, "yyyy-MM-dd");
      const nowInOfficeTz = format(now, "yyyy-MM-dd");
      const isToday = endInOfficeTz === nowInOfficeTz;

      const startTime = format(new Date(pastSchedule.start), "p");
      const endTime = format(endDate, "p");
      const timeStr = `${startTime} → ${endTime}`;

      return {
        type: "past",
        schedule: pastSchedule,
        text: `was in the office ${isToday ? "at" : "on"} ${timeStr}`,
        resourceName: pastSchedule.resource.name,
        resourceId: pastSchedule.resource.id,
        officeName,
        floorName
      };
    }

    return null;
};

export function usePersonStatus(
  scheduleInfo: PersonScheduleInfo | null
): "in-office" | "in-meeting" | "remote" | "offline" {
  return useMemo(() => {
    if (scheduleInfo?.type === "current" && scheduleInfo.schedule) {
      const currentSchedule = scheduleInfo.schedule;

      if (currentSchedule.is_virtual || currentSchedule.online_meeting) {
        return "in-meeting";
      }

      switch (currentSchedule.presence_type) {
        case "in_person":
          return "in-office";
        case "remote":
          return "remote";
        case "out_of_office":
          return "offline";
        default:
          if (currentSchedule.presence_status === "accepted") {
            return "in-office";
          }
          return "offline";
      }
    }

    return "offline";
  }, [scheduleInfo]);
}
